import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./article.css";
import Amalgam from "../../assets/images/Amalgam-logo.png";
import LinkedIn from "../../assets/images/LinkedIn.png";
import Breadcrumb from "../../assets/components/BreadCrumb";
import { useMediaQuery } from "react-responsive";
import Arrow from "../../assets/images/Arrow1.png";

function MTBank() {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const { pathname } = useLocation();
  const sectionRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sections =
            entry.target.getElementsByClassName("content-section");
          Array.from(sections).forEach((section) => {
            if (entry.isIntersecting) {
              section.classList.add("animate");
            } else {
              section.classList.remove("animate");
            }
          });
        });
      },
      {
        threshold: 0.2,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <div className="article-container">
      <div>
        <Breadcrumb route="/case-studies" pageTitle="M&T Bank" />
      </div>
      <section className="article-row-1" id="pearlx-background">
        <p>Financial Services</p>
        <h2>M&T Bank</h2>
        <div className="article-details fade-in-right">
          <div className="article-detail-item">
            <span>Location:  New York State 🇺🇲</span>
          </div>
        </div>
      </section>

      <section className="article-intro-section">
        <h1>
          How We Helped M&T Bank Navigate a Complex Legacy System Replacement
        </h1>
        <p>
          M&T Bank is one of the largest commercial banks in the United States,
          offering financial solutions across retail, commercial, and
          institutional markets. With a long-standing reputation for service and
          stability, the bank is focused on modernizing its digital
          infrastructure to better serve evolving customer needs.
        </p>
      </section>

      <section className="article-row-2" ref={sectionRef}>
        <div className="content-section">
          <h3>Challenge</h3>
          <p>
            M&T's business banking experience operated on Voyager: a legacy
            platform. When its vendor, FinServ, announced that it would
            discontinue support, M&T faced a mission-critical decision:
            modernize or risk operational vulnerabilities.
          </p>
        </div>
        <div className="content-section">
          <h3>Approach</h3>
          <p>
            As a strategic implementation partner, we provided skilled
            consultants to help M&T transition from their legacy system to Q2.
            We worked closely with their team to ensure seamless integration
            with existing tools and delivered comprehensive change management
            support, including stakeholder communications and training.
          </p>
        </div>
        <div className="content-section">
          <h3>Results</h3>
          <p>
            Our consultants supported M&T Bank through a successful digital
            transformation, replacing the aging Voyager platform with Q2’s
            modern, cloud-based solution. Throughout the project, we ensured
            alignment across IT, operations, and business teams—building a
            future-ready digital foundation that was technically sound,
            cost-efficient, and built for long-term agility.
          </p>
        </div>
      </section>
      <section className="article-section-6">
        <h2>The Challenge</h2>
        <h3>
          M&T faced a high-stakes decision: replace a deeply integrated legacy
          system or risk operational vulnerabilities. But they encountered three
          major hurdles:
        </h3>
        <ul
          style={{
            fontSize: isMobile ? "1rem" : "1.1rem",
            lineHeight: isMobile ? "1.5" : "1.6",
            marginBottom: isMobile ? "1rem" : "1.5rem",
            paddingLeft: isMobile ? "1.5rem" : "2rem",
          }}
        >
          <li>
            <strong>Complexity:</strong> Voyager was deeply embedded in M&T
            Bank's operations, with cross-departmental dependencies extending
            across IT, operations, compliance, and business units, making the
            replacement technically and operationally challenging.
          </li>
          <li>
            <strong>Delays in vendor selection:</strong> Internal alignment and
            platform comparisons slowed down the transformation timeline.
          </li>
          <li>
            <strong>Over-reliance on external tools:</strong> High licensing
            costs and obsolete tech were holding them back.
          </li>
        </ul>

        <p>
          They needed a partner who could adapt to shifting priorities without
          losing momentum, deliver technical excellence, and integrate
          seamlessly into their internal teams. That's where we came in.
        </p>
      </section>

      <section className="article-section-3">
        <h2>The Solution</h2>
        <h3>Our Approach:</h3>
        <p>
          We operated as an extension of the M&T team, steering the transition
          from Voyager to a modern, cloud-based digital banking platform.
        </p>
        <p>
          Our first step was vendor selection. We conducted side-by-side
          assessments of multiple platforms. Leveraging a matrix of over 130
          business use cases, our consultants contributed to the evaluation of
          multiple providers across scalability, security, user experience, and
          integration capabilities. After a thorough assessment, we identified
          the best option for flexibility and robust customization support.
        </p>
        <p>
          Because the vendor selection for the Voyager replacement required more
          time, we temporarily shifted our focus to aid M&T in increasing
          agility and reducing infrastructure costs by replacing Tibco
          middleware with a modern, Spring Boot-based microservices framework.
          This allowed our client to build integrations using scalable,
          maintainable, API-first components.
        </p>
        <p>
          We also delivered a change management strategy that included internal
          training programs, clear stakeholder communication, and operational
          documentation, smoothing adoption across business and technical teams.
        </p>
        <p>
          Next, we tackled implementation and integration. We worked towards
          seamlessly embedding the selected solution within M&T's digital
          ecosystem. This included:
        </p>

        <ul
          style={{
            listStyle: "none",
            paddingLeft: isMobile ? "1.5rem" : "2rem",
            fontSize: isMobile ? "1rem" : "1.1rem",
            lineHeight: isMobile ? "1.5" : "1.6",
          }}
        >
          <li style={{ position: "relative", marginBottom: "1rem" }}>
            <span style={{ position: "absolute", left: "-1.5rem" }}>—</span>
            Building secure integrations with core banking systems
          </li>
          <li style={{ position: "relative", marginBottom: "1rem" }}>
            <span style={{ position: "absolute", left: "-1.5rem" }}>—</span>
            Developing batch processes to ensure real-time and overnight data
            synchronization
          </li>
          <li style={{ position: "relative", marginBottom: "1rem" }}>
            <span style={{ position: "absolute", left: "-1.5rem" }}>—</span>
            Migrating historical and live data with validated mapping and
            transformation
          </li>
        </ul>

        <h3>Our Step-by-Step Process:</h3>
        <ul
          style={{
            fontSize: isMobile ? "1rem" : "1.1rem",
            lineHeight: isMobile ? "1.5" : "1.6",
            marginBottom: isMobile ? "1rem" : "1.5rem",
            paddingLeft: isMobile ? "1.5rem" : "2rem",
          }}
        >
          <li>Led the evaluation of multiple digital banking vendors</li>
          <li>Mapped platform capabilities to 130+ use cases</li>
          <li>
            Finalized the chosen solution as M&T’s digital banking platform
          </li>
          <li>
            Replaced Tibco middleware with a Spring Boot-based microservices
            architecture, improving system performance and maintainability
          </li>
          <li>
            Built and deployed RESTful APIs to connect Q2 to various internal
            and third-party systems
          </li>
          <li>
            We helped with the migration of business banking data from Voyager
            to Q2
          </li>
          <li>
            Built batch data pipelines and integration APIs for real-time and
            overnight syncing
          </li>
          <li>
            Delivered change management support including training,
            documentation, and rollout communication
          </li>
        </ul>

        <h3>The Results</h3>
        <p>
          M&T transitioned from an aging legacy system to a scalable, modern
          digital banking environment—one that supports evolving business needs
          and enhances customer experience.
        </p>
        <p>Amalgam’s leadership and technical guidance helped the bank:</p>
        <ul
          style={{
            fontSize: isMobile ? "1rem" : "1.1rem",
            lineHeight: isMobile ? "1.5" : "1.6",
            marginBottom: isMobile ? "1rem" : "1.5rem",
            paddingLeft: isMobile ? "1.5rem" : "2rem",
          }}
        >
          <li>
            Select and implement a future-ready digital banking platform (Q2)
          </li>
          <li>
            Modernize enterprise integration with open-source microservices
          </li>
          <li>Reduce dependency on high-cost middleware (Tibco)</li>
          <li>Improve system performance, flexibility, and scalability</li>
          <li>
            Facilitate data migration, integrity validation, and compliance
          </li>
          <li>Train internal teams and support cross-departmental adoption</li>
        </ul>
      </section>

      <section className="article-section-4">
        <h2>Conclusions</h2>

        <h3>Summary of Benefits</h3>
        <p>
          This wasn’t a routine platform replacement: it was a complex and
          impactful enterprise-scale transformation. Amalgam provided the
          agility, technical depth, and strategic foresight M&T needed to
          modernize with confidence.
        </p>
        <p>
          From vendor selection to data migration and change enablement, we
          helped M&T lay the foundation for a new era of digital banking.
        </p>

        <h3>Contact Information</h3>
        <p>
          Are you ready to leverage cutting-edge technology for your
          organization's needs? Contact us at{" "}
          <a href="mailto:hello@amalgam-inc.com">hello@amalgam-inc.com</a>.
        </p>
      </section>

      <section className="ourwork-row-14 blog-row-14">
        <h2>Get in touch</h2>
        <p>
          Reach out to us to gather more information or{" "}
          <br className="blog-row-14-break" /> set our wheels in motion.
        </p>
        <div className="ourwork-arrow-box blog-arrow-box">
          <img src={Arrow} alt="arrow" className="ourwork-arrow" />
          <button onClick={() => navigate("/contactus")}>Let's talk</button>
        </div>
      </section>
      {!isMobile ? (
        <section className="home-footer">
          <div className="home-footer-middle">
            <img src={Amalgam} alt="Amalgam" className="home-footer-amalgam" />
            <Link to="/" className="home-footer-links">
              Home
            </Link>
            <Link to="/ourwork" className="home-footer-links">
              Our Work
            </Link>
            <Link to="/aboutus" className="home-footer-links">
              About Us
            </Link>
            <Link to="/case-studies" className="home-footer-links">
              Case Studies
            </Link>
            <Link to="/startups" className="home-footer-links">
              Launchpad
            </Link>
            <Link to="/careers" className="home-footer-links">
              Careers
            </Link>
            <Link to="/contactus" className="home-footer-links">
              Contact Us
            </Link>
            <img
              src={LinkedIn}
              alt="Instagram"
              className="home-footer-linkedin"
            />
          </div>
          <div className="home-footer-dividor">
            <div className="home-footer-policies">
              <p>
                <Link to="/privacy-policy">Privacy Policy</Link>
              </p>
              <p>
                <Link to="/terms-and-conditions">Terms and Conditions</Link>
              </p>
              <p>
                <Link to="/cookie-policy">Cookie Policy</Link>
              </p>
            </div>
          </div>
        </section>
      ) : (
        <section>
          <div className="footer-minimal">
            <div className="footer-content">
              {/* <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px' }}>
                <img src={Amalgam} alt="Amalgam" className="footer-logo" />
                <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' style={{ alignSelf: "center" }} />
              </div> */}
              <div className="footer-top-row">
                <Link to="/" className="footer-link">
                  Home
                </Link>
                <span className="footer-separator">•</span>
                <Link to="/ourwork" className="footer-link">
                  Our Work
                </Link>
                <span className="footer-separator">•</span>
                <Link to="/aboutus" className="footer-link">
                  About Us
                </Link>
                <span className="footer-separator">•</span>
                <Link to="/case-studies" className="footer-link">
                  Case Studies
                </Link>
              </div>
              <div className="footer-bottom-row">
                <Link to="/startups" className="footer-link">
                  Launchpad
                </Link>
                <span className="footer-separator">•</span>
                <Link to="/careers" className="footer-link">
                  Careers
                </Link>
                <span className="footer-separator">•</span>
                <Link to="/contactus" className="footer-link">
                  Contact
                </Link>
              </div>
            </div>
          </div>

          <div
            style={{
              width: "80%",
              height: "0px",
              borderTop: "1px solid black",
              backgroundColor: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingBottom: "20px",
              margin: "0 auto",
            }}
          ></div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "white",
              gap: isMobile ? "5px" : "20px",
              paddingBottom: "20px",
              width: "100%",
              padding: "0 10px 20px 10px",
              textAlign: "center",
            }}
          >
            <Link
              to="/privacy-policy"
              style={{
                textAlign: "center",
                color: "black",
                textDecoration: "none",
              }}
            >
              Privacy Policy
            </Link>
            <span style={{ color: "black", fontSize: "14px" }}>|</span>
            <Link
              to="/terms-and-conditions"
              style={{ color: "black", textDecoration: "none" }}
            >
              Terms and Conditions
            </Link>
            <span style={{ color: "black", fontSize: "14px" }}>|</span>
            <Link
              to="/cookie-policy"
              style={{
                fontSize: "14px",
                color: "black",
                textDecoration: "none",
              }}
            >
              Cookie Policy
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

export default MTBank;
